import { PATH } from '@old/data/core/paths';
import { Box, Link, Menu, MenuItem, Collapse, Tooltip } from '@mui/material';
import { Stack } from '@mui/system';
import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router';
import i18n from '../../../libs/language';
import { useService } from '@old/hooks/use-service';
import { getInconsistenciesSummary } from '@old/services/domain/page/inconsistencies/get-inconsistencies-summary';
import { useGlobalStore } from '@old/global-store/use-store';
import { useEffectOnUpdate } from '@old/hooks/core';
import DashboardIcon from '@old/assets/svg/main-menu/dashboard.svg';
import DashboardActiveIcon from '@old/assets/svg/main-menu/dashboard-active.svg';
import MapIcon from '@old/assets/svg/main-menu/map.svg';
import MapActiveIcon from '@old/assets/svg/main-menu/map-active.svg';
import SilobagIcon from '@old/assets/svg/main-menu/silobag.svg';
import SilobagActiveIcon from '@old/assets/svg/main-menu/silobag-active.svg';
import SilobagV2Icon from '@old/assets/svg/main-menu/silobag-v2.svg';
import ReportIcon from '@old/assets/svg/main-menu/report.svg';
import ReportActiveIcon from '@old/assets/svg/main-menu/report-active.svg';
import InconsistenciesIcon from '@old/assets/svg/main-menu/inconsistencies.svg';
import InconsistenciesActiveIcon from '@old/assets/svg/main-menu/inconsistencies-active.svg';
import { BetaBadge } from '@old/ui/beta-badge';

type MainMenuType = {
  isMobile?: boolean;
  setOpenReportSat: (open: boolean) => void;
};

export function MainMenu({ isMobile = false, setOpenReportSat }: MainMenuType) {
  const { totalDiscrepancies, setTotalDiscrepancies, organizationId } = useGlobalStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCustomOptions, setOpenCustomOptions] = useState(false);
  const open = Boolean(anchorEl);
  const location = useLocation();
  const navigate = useNavigate();
  //TODO useEffectOnmount con estado global
  //TODO: Se esta refecheando cada vez que cambias de ruta y no deberia, el componente se vuelve montar
  const { summary: summaryTotals, trigger } = useService(getInconsistenciesSummary, {
    fetchOnMount: true,
    onSuccess: (data) => {
      setTotalDiscrepancies(data.total_discrepancies);
    },
  });

  useEffectOnUpdate(() => {
    trigger();
  }, [organizationId]);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setOpenCustomOptions(false);
    setAnchorEl(null);
  };

  const style = {
    color: '#000',
    textDecoration: 'none',
    fontSize: '0.8rem',
    '&.active': {
      textDecoration: 'none',
      fontWeight: 600,
    },
    '&:hover': { color: '#6baa00' },
    '&.Mui-selected': {
      color: '#6baa00',
      backgroundColor: 'rgba(107, 170, 0, 0.12)',
    },
  };

  const PATHS_REPORTS: string[] = [
    PATH.REPORT_STOCK,
    PATH.REPORT_DEVICE,
    PATH.REPORT_ACTIVITY,
    PATH.REPORT_STATE_STOCK,
    PATH.REPORT_SAT_SILOBAGS_EXISTENCE,
  ];

  const isReport = PATHS_REPORTS.includes(location.pathname);

  return (
    <Stack
      direction={{ sm: 'column', md: 'row' }}
      sx={{
        display: 'flex',
        flex: 1,
        gridDirection: 'row',
        alignItems: !isMobile ? 'center' : 'start',
        justifyContent: 'flex-start',
        minWidth: !isMobile ? 400 : 50,
        height: 'auto',
      }}
      spacing={1}
      mt={!isMobile ? 0 : 2}
    >
      <Tooltip title={i18n.t('app.navbar.dashboard')} arrow>
        <Link
          component={NavLink}
          to={PATH.DASHBOARD}
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',

            '& .default-icon, & .active-icon': {
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            },

            '& .active-icon': {
              opacity: 0,
            },

            // Hover
            '&:hover .default-icon': {
              opacity: 0,
            },

            '&:hover .active-icon': {
              opacity: 1,
            },

            // Ruta activa
            '&.active .default-icon': {
              opacity: 0,
            },

            '&.active .active-icon': {
              opacity: 1,
            },

            ...style,
          }}
        >
          <img className="default-icon" src={DashboardIcon} alt="Dashboard" width={30} height={30} />

          <img
            className="active-icon"
            src={DashboardActiveIcon}
            alt="DashboardActive"
            width={30}
            height={30}
          />
        </Link>
      </Tooltip>
      <Tooltip title={i18n.t('app.navbar.map')} arrow>
        <Link
          component={NavLink}
          to={PATH.MAP}
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',

            '& .default-icon, & .active-icon': {
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            },

            '& .active-icon': {
              opacity: 0,
            },

            // Hover
            '&:hover .default-icon': {
              opacity: 0,
            },

            '&:hover .active-icon': {
              opacity: 1,
            },

            // Ruta activa
            '&.active .default-icon': {
              opacity: 0,
            },

            '&.active .active-icon': {
              opacity: 1,
            },

            ...style,
          }}
        >
          <img className="default-icon" src={MapIcon} alt="Map" width={30} height={30} />

          <img className="active-icon" src={MapActiveIcon} alt="MapActive" width={30} height={30} />
        </Link>
      </Tooltip>

      <Tooltip title={i18n.t('app.navbar.silobags')} arrow>
        <Link
          component={NavLink}
          to={PATH.SILOBAG}
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',

            '& .default-icon, & .active-icon': {
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            },

            '& .active-icon': {
              opacity: 0,
            },

            // Hover
            '&:hover .default-icon': {
              opacity: 0,
            },

            '&:hover .active-icon': {
              opacity: 1,
            },

            // Ruta activa
            '&.active .default-icon': {
              opacity: 0,
            },

            '&.active .active-icon': {
              opacity: 1,
            },

            ...style,
          }}
        >
          <img className="default-icon" src={SilobagIcon} alt="Silobags" width={30} height={30} />

          <img className="active-icon" src={SilobagActiveIcon} alt="SilobagsActive" width={30} height={30} />
        </Link>
      </Tooltip>
      <Tooltip
        arrow
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{i18n.t('app.navbar.silobags')}</span>
            <BetaBadge />
          </Box>
        }
      >
        <Link
          component={NavLink}
          to={PATH.SILOBAG_V2}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            width: 40,
            height: 40,

            '& .default-icon, & .active-icon': {
              position: 'absolute',
              transition: 'opacity 0.2s ease',
            },

            '& .active-icon': {
              opacity: 0,
            },

            '&:hover .default-icon': {
              opacity: 0,
            },

            '&:hover .active-icon': {
              opacity: 1,
            },

            '&.active .default-icon': {
              opacity: 0,
            },

            '&.active .active-icon': {
              opacity: 1,
            },

            ...style,
          }}
        >
          <img className="default-icon" src={SilobagV2Icon} alt="SilobagV2" width={30} height={30} />

          <img className="active-icon" src={SilobagActiveIcon} alt="SilobagV2Active" width={30} height={30} />
        </Link>
      </Tooltip>
      <Box
        sx={{
          ...style,
          cursor: 'pointer',

          '& .default-icon, & .active-icon': {
            position: 'absolute',
            transition: 'opacity 0.2s ease',
          },

          '& .active-icon': {
            opacity: 0,
          },

          '&:hover .default-icon': {
            opacity: 0,
          },

          '&:hover .active-icon': {
            opacity: 1,
          },

          '&.active .default-icon': {
            opacity: 0,
          },

          '&.active .active-icon': {
            opacity: 1,
          },
        }}
        className={isReport ? 'active' : ''}
      >
        <Tooltip title={i18n.t('app.navbar.reports')} arrow>
          <Box
            onClick={handleClick}
            sx={{
              position: 'relative',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img className="default-icon" src={ReportIcon} alt="Reports" width={30} height={30} />

            <img className="active-icon" src={ReportActiveIcon} alt="ReportsActive" width={30} height={30} />
          </Box>
        </Tooltip>

        <Menu open={open} anchorEl={anchorEl} onClose={handleClose} aria-selected="true">
          <MenuItem
            sx={style}
            selected={location.pathname === PATH.REPORT_STOCK}
            onClick={() => {
              navigate(PATH.REPORT_STOCK);
              handleClose();
            }}
          >
            {i18n.t('app.navbar.stock')}
          </MenuItem>

          <MenuItem
            sx={style}
            selected={location.pathname === PATH.REPORT_DEVICE}
            onClick={() => {
              navigate(PATH.REPORT_DEVICE);
              handleClose();
            }}
          >
            {i18n.t('app.navbar.devices')}
          </MenuItem>

          <MenuItem
            sx={style}
            selected={location.pathname === PATH.REPORT_ACTIVITY}
            onClick={() => {
              navigate(PATH.REPORT_ACTIVITY);
              handleClose();
            }}
          >
            {i18n.t('app.navbar.activity')}
          </MenuItem>

          <MenuItem
            sx={style}
            selected={location.pathname === PATH.REPORT_STATE_STOCK}
            onClick={() => {
              navigate(PATH.REPORT_STATE_STOCK);
              handleClose();
            }}
          >
            {i18n.t('app.navbar.state_stock')}
          </MenuItem>

          <MenuItem
            sx={style}
            selected={location.pathname === PATH.REPORT_SAT_SILOBAGS_EXISTENCE}
            onClick={() => {
              navigate(PATH.REPORT_SAT_SILOBAGS_EXISTENCE);
              handleClose();
            }}
          >
            {i18n.t('app.navbar.existence_stock')}
          </MenuItem>

          <MenuItem
            sx={style}
            onMouseEnter={() => setOpenCustomOptions(true)}
            onClick={() => setOpenCustomOptions(!openCustomOptions)}
          >
            {i18n.t('app.navbar.personalized')}
          </MenuItem>

          <Collapse in={openCustomOptions} timeout="auto" unmountOnExit>
            <MenuItem
              sx={{ ...style, pl: 2 }}
              selected={location.pathname === PATH.REPORT_POSITION_GENERAL}
              onClick={() => {
                navigate(PATH.REPORT_POSITION_GENERAL);
                handleClose();
              }}
            >
              {i18n.t('app.navbar.general_position')}
            </MenuItem>

            <MenuItem
              sx={{ ...style, pl: 2 }}
              onClick={() => {
                setOpenReportSat(true);
                handleClose();
              }}
            >
              Silosat
            </MenuItem>
          </Collapse>
        </Menu>
      </Box>
      <Tooltip title={i18n.t('app.inconsistencies_page.inconsistencies')} arrow>
        <Link
          component={NavLink}
          to={PATH.INCONSISTENCIES}
          sx={{
            ...style,

            '& .default-icon, & .active-icon': {
              transition: 'opacity 0.2s ease',
            },

            '& .active-icon': {
              opacity: 0,
            },

            '&:hover .default-icon': {
              opacity: 0,
            },

            '&:hover .active-icon': {
              opacity: 1,
            },

            '&.active .default-icon': {
              opacity: 0,
            },

            '&.active .active-icon': {
              opacity: 1,
            },
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Box
              sx={{
                position: 'relative',
                width: 30,
                height: 28,
              }}
            >
              <img
                className="default-icon"
                src={InconsistenciesIcon}
                alt={i18n.t('app.inconsistencies_page.inconsistencies')}
                width={30}
                height={32}
                style={{ position: 'absolute', top: 0, left: 0 }}
              />

              <img
                className="active-icon"
                src={InconsistenciesActiveIcon}
                alt={i18n.t('app.inconsistencies_page.inconsistencies')}
                width={30}
                height={32}
                style={{ position: 'absolute', top: 0, left: 0 }}
              />
            </Box>

            {totalDiscrepancies > 0 && (
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: -4,
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ea1818',
                  color: 'white',
                  borderRadius: '50%',
                  fontSize: '9px',
                  minWidth: '16px',
                  width: 16,
                  height: 16,
                  fontWeight: 600,
                }}
              >
                {summaryTotals?.total_discrepancies ?? '―'}
              </Box>
            )}
          </Box>
        </Link>
      </Tooltip>
    </Stack>
  );
}
